import './Button.scss';
import clsx from 'clsx';
import React, { ComponentPropsWithoutRef, ElementType, memo, PropsWithChildren, ReactElement, ReactNode } from 'react';
import { LoadingIcon } from 'src/modules/common/icons/LoadingIcon';

type PolymorphicProps<E extends ElementType> = PropsWithChildren<ComponentPropsWithoutRef<E> & {
  as?: E;
}>;

type ButtonProps<T extends React.ElementType> = PolymorphicProps<T> & PropsWithChildren<{
  kind?: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  iconOnly?: boolean;
  fullWidth?: boolean;
  loading?: boolean;
  reversed?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  className?: string;
}>;

const ButtonWithoutMemo = <T extends ElementType = 'button'>({
  as,
  className = '',
  disabled = false,
  kind = 'primary',
  size = 'md',
  iconOnly = false,
  fullWidth = false,
  loading = false,
  reversed = false,
  icon,
  children,
  ...props
}: ButtonProps<T>): ReactElement => {
  const Component = as || 'button';

  return (
    <Component
      className={clsx({
        'bp-button': true,
        'bp-button--full-width': fullWidth,
        'bp-button--reversed': reversed,
        [`bp-button--${kind}`]: true,
        [`bp-button--${kind}-disabled`]: disabled || loading,
        [`bp-button--${size}`]: true,
        [`bp-button--${size}-icon-only`]: iconOnly,
      }, className)}
      disabled={disabled || loading}
      type={props.type || 'button'}
      {...props}
    >
      {children}

      {loading ? <LoadingIcon className="bp-button__loading-icon"/> : icon}
    </Component>
  );
};

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const Button = memo(ButtonWithoutMemo) as typeof ButtonWithoutMemo;
